import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import { createPortal } from "react-dom";
import { Bell, CheckCircle2, ChevronDown, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import { traceAsync } from "../utils/perfTrace";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const SMS_CONSENT_COPY =
  "Yes, I want to receive automated text message notifications from Strobe Aviation about new broker requests, broker request edits, broker request deletes, and project status activity. Message frequency varies based on account activity. Message and data rates may apply. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com. Consent is not required to use the admin portal.";

const cleanTail = (value) => value || "a request";

export default function AdminNotifications({ className = "", panelClass = PANEL }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState(null);

  const [enabledEmail, setEnabledEmail] = useState(false);
  const [enabledSMS, setEnabledSMS] = useState(false);
  const [newRequests, setNewRequests] = useState(true);
  const [brokerEdits, setBrokerEdits] = useState(true);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [listening, setListening] = useState(false);
  const channelRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const prefsRef = useRef({ wants_new_requests: true, wants_broker_edits: true });

  const bellIsOn = enabledEmail || enabledSMS;
  const hasEventEnabled = newRequests || brokerEdits;

  const showNotice = useCallback((nextNotice) => {
    setNotice(nextNotice);
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setNotice(null), 8000);
  }, []);

  const stopRealtime = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    setListening(false);
  }, []);

  const fetchTailForIntake = useCallback(async (intakeId) => {
    if (!intakeId) return null;
    const { data, error } = await supabase
      .from("broker_intake")
      .select("tail_number")
      .eq("id", intakeId)
      .maybeSingle();

    if (error) {
      console.error("Failed to load intake for admin notification:", error);
      return null;
    }

    return data?.tail_number || null;
  }, []);

  const startRealtime = useCallback(() => {
    stopRealtime();

    const channel = supabase
      .channel("admin_portal_notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "broker_intake",
        },
        (payload) => {
          if (!prefsRef.current.wants_new_requests) return;
          showNotice({
            title: "New broker request",
            body: `${cleanTail(payload.new?.tail_number)} was submitted.`,
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "broker_project_change_log",
        },
        async (payload) => {
          if (!prefsRef.current.wants_broker_edits) return;
          const changeType = payload.new?.change_type || "update";
          const tail =
            payload.new?.old_values?.tail_number ||
            payload.new?.new_values?.tail_number ||
            (await fetchTailForIntake(payload.new?.intake_id));
          const actorType = payload.new?.actor_type === "admin" ? "Admin" : "Broker";
          const actorName = formatDisplayName(payload.new?.actor_name) || actorType;

          showNotice({
            title: changeType === "delete" ? `${actorType} deleted a request` : `${actorType} edited a request`,
            body: `${actorName} ${changeType === "delete" ? "deleted" : "updated"} ${cleanTail(tail)}.`,
          });
        }
      )
      .subscribe((status) => {
        setListening(status === "SUBSCRIBED");
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setMessage("Notification preferences are saved, but live admin alerts are not connected.");
        }
      });

    channelRef.current = channel;
  }, [fetchTailForIntake, showNotice, stopRealtime]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const { data: authData, error: authError } = await traceAsync(
        "notifications.admin.getUser",
        () => supabase.auth.getUser(),
        {}
      );
      const user = authData?.user;
      if (!alive) return;

      if (authError || !user) {
        setLoading(false);
        return;
      }

      const { data, error } = await traceAsync(
        "notifications.admin.prefs",
        () => supabase
          .from("admin_notification_prefs")
          .select("email, phone, wants_email, wants_sms, wants_new_requests, wants_broker_edits")
          .eq("user_id", user.id)
          .maybeSingle(),
        {}
      );

      if (!alive) return;

      if (error) {
        console.error("Failed to load admin notification preferences:", error);
        setMessage("Could not load admin notification preferences.");
        setLoading(false);
        return;
      }

      const wantsNewRequests = data?.wants_new_requests ?? true;
      const wantsBrokerEdits = data?.wants_broker_edits ?? true;

      const wantsEmail = data ? !!data.wants_email : true;
      setEnabledEmail(wantsEmail);
      setEnabledSMS(!!data?.wants_sms);
      setNewRequests(wantsNewRequests);
      setBrokerEdits(wantsBrokerEdits);
      setEmail(data?.email || user.email || "");
      setPhone(data?.phone || "");
      prefsRef.current = {
        wants_new_requests: wantsNewRequests,
        wants_broker_edits: wantsBrokerEdits,
      };
      setLoading(false);

      if ((wantsEmail || data?.wants_sms) && (wantsNewRequests || wantsBrokerEdits)) {
        traceAsync("notifications.admin.realtime", () => Promise.resolve(startRealtime()), {});
      }
    })();

    return () => {
      alive = false;
      stopRealtime();
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    };
  }, [startRealtime, stopRealtime]);

  async function savePrefs() {
    setMessage("");

    const wants_email = enabledEmail;
    const wants_sms = enabledSMS;
    const wants_new_requests = newRequests;
    const wants_broker_edits = brokerEdits;
    const cleanEmail = email.trim();
    const cleanPhone = phone.trim();

    if (!wants_email && !wants_sms) {
      setMessage("Choose email, SMS, or both before saving.");
      return;
    }
    if (!wants_new_requests && !wants_broker_edits) {
      setMessage("Choose at least one admin event type.");
      return;
    }
    if (wants_email && !cleanEmail) {
      setMessage("Enter an email address for email notifications.");
      return;
    }
    if (wants_sms && !cleanPhone) {
      setMessage("Enter a mobile phone number for SMS notifications.");
      return;
    }

    setSaving(true);
    const { data: authData, error: authError } = await supabase.auth.getUser();
    const user = authData?.user;

    if (authError || !user) {
      setSaving(false);
      setMessage("You must be signed in to save notification preferences.");
      return;
    }

    const { error } = await supabase
      .from("admin_notification_prefs")
      .upsert(
        {
          user_id: user.id,
          email: cleanEmail || null,
          phone: cleanPhone || null,
          wants_email,
          wants_sms,
          wants_new_requests,
          wants_broker_edits,
        },
        { onConflict: "user_id" }
      );

    if (error) {
      console.error("Failed to save admin notification preferences:", error);
      setSaving(false);
      setMessage("Could not save admin notification preferences.");
      return;
    }

    prefsRef.current = { wants_new_requests, wants_broker_edits };
    if (wants_email || wants_sms) {
      startRealtime();
    } else {
      stopRealtime();
    }

    setSaving(false);
    setMessage("Admin notification preferences saved.");
    setOpen(false);
  }

  const statusText = useMemo(() => {
    if (loading) return "Loading admin notification settings";
    if (!bellIsOn || !hasEventEnabled) return "Admin notifications off";
    if (listening) return "Admin notifications enabled";
    return "Admin notifications saved; live updates connecting";
  }, [bellIsOn, hasEventEnabled, listening, loading]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`${panelClass} ${className} p-2 hover:bg-white transition inline-flex items-center justify-center relative`}
        aria-label="Admin notification settings"
        title={statusText}
        type="button"
      >
        <Bell className={`w-5 h-5 ${bellIsOn && hasEventEnabled ? "text-[#EB040D]" : "text-[#1A1A1A]"}`} />
        {bellIsOn && hasEventEnabled && listening && (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#EB040D]" />
        )}
      </button>

      {notice && (
        <div className="fixed right-4 top-4 z-50 w-[min(92vw,380px)] rounded-xl border border-black/10 bg-white p-4 shadow-2xl">
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="absolute right-2 top-2 rounded p-1 text-black/50 hover:bg-black/5 hover:text-black"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex gap-3 pr-5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
            <div>
              <p className="text-sm font-semibold text-[#1A1A1A]">{notice.title}</p>
              <p className="mt-1 text-sm text-black/65">{notice.body}</p>
            </div>
          </div>
        </div>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[1000] overflow-y-auto p-3 sm:p-6">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative mx-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-[680px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)]">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 z-10 rounded-lg bg-black/[0.03] p-2 text-black/70 hover:bg-black/[0.06] hover:text-black"
              aria-label="Close"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="border-b border-black/10 px-5 py-4 sm:px-6">
              <h2 className="text-lg font-semibold">Admin Notification Preferences</h2>
              <p className="mt-1 max-w-xl text-sm text-black/60">
                Choose which Strobe admin events should trigger in-browser, email, and SMS notifications.
              </p>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-black/45">Events</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex min-h-[68px] items-center gap-3 rounded-xl border border-black/10 bg-black/[0.015] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={newRequests}
                      onChange={(e) => setNewRequests(e.target.checked)}
                      className="h-4 w-4 accent-[#EB040D]"
                    />
                    <span className="text-sm font-medium">New broker requests</span>
                  </label>
                  <label className="flex min-h-[68px] items-center gap-3 rounded-xl border border-black/10 bg-black/[0.015] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={brokerEdits}
                      onChange={(e) => setBrokerEdits(e.target.checked)}
                      className="h-4 w-4 accent-[#EB040D]"
                    />
                    <span className="text-sm font-medium">Broker edits and deletes</span>
                  </label>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-black/45">Delivery</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={enabledEmail}
                        onChange={(e) => setEnabledEmail(e.target.checked)}
                        className="h-4 w-4 accent-[#EB040D]"
                      />
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@strobeaviation.com"
                      className="w-full rounded-lg border border-black/10 px-3 py-2.5 text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={enabledSMS}
                        onChange={(e) => setEnabledSMS(e.target.checked)}
                        className="h-4 w-4 accent-[#EB040D]"
                      />
                      Mobile phone (SMS)
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="w-full rounded-lg border border-black/10 px-3 py-2.5 text-sm"
                    />
                  </div>
                </div>
              </div>

              <details className="group rounded-xl border border-black/10 bg-black/[0.015] text-xs leading-relaxed text-black/65">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-black/75 [&::-webkit-details-marker]:hidden">
                  <span>SMS consent details</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-black/45 transition group-open:rotate-180" />
                </summary>
                <div className="border-t border-black/10 px-4 pb-4 pt-3">
                  <p>{SMS_CONSENT_COPY}</p>
                  <p className="mt-3">
                    <span className="font-medium text-black/70">Message frequency:</span> varies by account activity.
                    <span className="ml-2 font-medium text-black/70">Standard rates:</span> message and data rates may apply.
                  </p>
                  <p className="mt-2">
                    <a className="text-[#EB040D] underline underline-offset-2" href="/terms" target="_blank" rel="noreferrer">
                      Terms of Service
                    </a>
                    <span className="mx-1 text-black/30">|</span>
                    <a className="text-[#EB040D] underline underline-offset-2" href="/privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </details>

              {message && (
                <p className="rounded-md bg-black/[0.04] px-3 py-2 text-xs text-black/70">
                  {message}
                </p>
              )}
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-black/10 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg border border-black/10 px-4 py-2.5 text-sm hover:bg-black/[0.04]"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={savePrefs}
                disabled={loading || saving}
                className="rounded-lg bg-[#EB040D] px-4 py-2.5 text-sm text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                type="button"
              >
                {saving ? "Saving..." : bellIsOn ? "Save" : "Enable Notifications"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
